import { createApi } from '@reduxjs/toolkit/query/react';
import { createTimelyBaseQuery, type TimelyBaseQueryMeta } from 'api/core/base';
import TimelyLogo from 'images/apps_logo/timely.svg';
import { appendTasks } from 'redux/modules/tic/tasks/tic.tasks.slice';
import type {
  LinkedWorkItem,
  LinkedWorkItemsPage,
  LinkedWorkItemsQueryParams,
} from 'types/linkedWorkItem';
import { linkedWorkItemToPresentableTask } from './linkedWorkItems.helpers';

/**
 * RTK Query API slice for native linked work items (Rails-backed, NOT TIC).
 * Uses Timely's standard authentication via createTimelyBaseQuery, which also prefixes the
 * current account id (`/1.1/:account_id/linked_work_items`).
 *
 * Every fetch is appended into the shared task pool (`ticTasks` slice) mapped as
 * `PresentableTaskResource`, mirroring how the TIC `getTasks`/`searchTasks` endpoints feed
 * the hour editor's linked-work picker. Items use their `icon_url` when present, otherwise
 * the Timely logo.
 */
export const linkedWorkItemsApi = createApi({
  reducerPath: 'linkedWorkItemsApi',
  baseQuery: createTimelyBaseQuery(),
  tagTypes: ['LinkedWorkItem'],
  endpoints: build => ({
    getLinkedWorkItems: build.query<LinkedWorkItemsPage, LinkedWorkItemsQueryParams>({
      query: ({ search, status, projectId, assignedUserId, limit, offset }) => ({
        url: 'linked_work_items',
        params: {
          ...(search != null ? { search } : {}),
          ...(status != null ? { status } : {}),
          ...(projectId != null ? { project_id: String(projectId) } : {}),
          ...(assignedUserId != null ? { assigned_user_id: String(assignedUserId) } : {}),
          ...(limit != null ? { limit: String(limit) } : {}),
          ...(offset != null ? { offset: String(offset) } : {}),
        },
      }),
      // The body is one page; the filtered total across all pages rides in `X-Total-Count`, which
      // is what tells a picker whether scrolling can load more.
      transformResponse: (
        items: Array<LinkedWorkItem> | undefined,
        meta: TimelyBaseQueryMeta | undefined
      ): LinkedWorkItemsPage => {
        const header = meta?.responseHeaders.get('X-Total-Count');
        const totalCount = header != null && header !== '' ? Number(header) : Number.NaN;
        return {
          items: items ?? [],
          totalCount: Number.isFinite(totalCount) ? totalCount : null,
        };
      },
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // Always dispatch (even for an empty page) so the pool transitions from
          // "not loaded" (null) to "loaded" for accounts without TIC integrations.
          dispatch(
            appendTasks({
              tasks: data.items.map(item => linkedWorkItemToPresentableTask(item, TimelyLogo)),
            })
          );
        } catch {}
      },
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: 'LinkedWorkItem' as const, id })),
              'LinkedWorkItem',
            ]
          : ['LinkedWorkItem'],
    }),
    getLinkedWorkItem: build.query<LinkedWorkItem, { id: number }>({
      query: ({ id }) => ({ url: `linked_work_items/${id}` }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(appendTasks({ tasks: [linkedWorkItemToPresentableTask(data, TimelyLogo)] }));
          }
        } catch {}
      },
      providesTags: (_result, _error, { id }) => [{ type: 'LinkedWorkItem', id }],
    }),
  }),
});

export const { useLazyGetLinkedWorkItemQuery } = linkedWorkItemsApi;
